import {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  format,
  parseISO,
  isValid,
  isBefore,
  startOfDay,
  isAfter,
} from "date-fns";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { cn } from "@/utils/cn";

export interface DateInputProps {
  id?: string;
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  min?: string;
  max?: string;
  readOnly?: boolean;
  className?: string;
  isDateBlocked?: (date: Date) => boolean;
  getBlockedReason?: (date: Date) => string | undefined;
}

export function DateInput({
  id,
  value,
  onChange,
  min,
  max,
  readOnly,
  className,
  isDateBlocked,
  getBlockedReason,
}: DateInputProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const selectedDate = useMemo(() => {
    if (!value) return null;
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }, [value]);

  const [viewMonth, setViewMonth] = useState(
    () => selectedDate ?? new Date()
  );

  const today = useMemo(() => startOfDay(new Date()), []);

  const minDate = useMemo(() => {
    if (!min) return null;
    const d = parseISO(min);
    return isValid(d) ? startOfDay(d) : null;
  }, [min]);

  const maxDate = useMemo(() => {
    if (!max) return null;
    const d = parseISO(max);
    return isValid(d) ? startOfDay(d) : null;
  }, [max]);

  const displayValue = useMemo(
    () => (selectedDate ? format(selectedDate, "MM/dd/yyyy") : ""),
    [selectedDate]
  );

  const calendarDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth));
    const end = endOfWeek(endOfMonth(viewMonth));
    const days: Date[] = [];
    let current = start;
    while (!isAfter(current, end)) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [viewMonth]);

  const dropdownPosition = useMemo(() => {
    if (!isOpen || !inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) {
      setViewMonth(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [isOpen]);

  const isDayDisabled = useCallback(
    (day: Date) => {
      if (minDate && isBefore(day, minDate)) return true;
      if (maxDate && isAfter(day, maxDate)) return true;
      if (isDateBlocked?.(day)) return true;
      return false;
    },
    [minDate, maxDate, isDateBlocked]
  );

  const handleDateSelect = useCallback(
    (day: Date) => {
      if (isDayDisabled(day)) return;
      onChange({ target: { value: format(day, "yyyy-MM-dd") } });
      setIsOpen(false);
    },
    [isDayDisabled, onChange]
  );

  const toggleOpen = useCallback(() => {
    if (readOnly) return;
    setIsOpen((prev) => !prev);
  }, [readOnly]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      const parsed = parseISO(raw);
      if (isValid(parsed) && !isDayDisabled(parsed)) {
        onChange({ target: { value: format(parsed, "yyyy-MM-dd") } });
      }
    },
    [isDayDisabled, onChange]
  );

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center">
        <input
          ref={inputRef}
          id={id}
          type="text"
          readOnly
          value={displayValue}
          placeholder="mm/dd/yyyy"
          onClick={toggleOpen}
          onChange={handleInputChange}
          className={cn(
            "w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400",
            !readOnly && "cursor-pointer",
            className
          )}
        />
        {!readOnly && (
          <button
            type="button"
            onClick={toggleOpen}
            className="-ml-8 flex items-center justify-center text-slate-400 hover:text-slate-600"
          >
            <Calendar className="h-4 w-4" />
          </button>
        )}
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="z-[100] w-72 rounded border border-slate-300 bg-white p-3 shadow-lg"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
            }}
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setViewMonth((m) => subMonths(m, 1))}
                className="rounded p-1 hover:bg-slate-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-medium">
                {format(viewMonth, "MMMM yyyy")}
              </span>
              <button
                type="button"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                className="rounded p-1 hover:bg-slate-100"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            <div className="mb-1 grid grid-cols-7 text-center text-xs font-medium text-slate-500">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                <div key={d}>{d}</div>
              ))}
            </div>

            <div className="grid grid-cols-7">
              {calendarDays.map((day) => {
                if (!isSameMonth(day, viewMonth)) {
                  return (
                    <div key={day.toISOString()} className="invisible h-8 w-8" />
                  );
                }

                const isSelected = selectedDate && isSameDay(day, selectedDate);
                const isToday = isSameDay(day, today);
                const blocked = isDateBlocked?.(day);
                const outOfRange =
                  (minDate && isBefore(day, minDate)) ||
                  (maxDate && isAfter(day, maxDate));
                const disabled = blocked || outOfRange;
                const blockedReason = blocked
                  ? getBlockedReason?.(day)
                  : undefined;

                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    disabled={!!disabled}
                    title={blockedReason}
                    onClick={() => handleDateSelect(day)}
                    className={cn(
                      "mx-auto flex h-8 w-8 items-center justify-center rounded text-sm",
                      !disabled && !isSelected && "hover:bg-slate-100 cursor-pointer",
                      isSelected && "bg-slate-900 text-white hover:bg-slate-800",
                      isToday && !isSelected && !disabled && "font-bold ring-1 ring-slate-400",
                      blocked && "bg-red-50 text-red-300 line-through cursor-not-allowed",
                      outOfRange && !blocked && "text-slate-300 cursor-not-allowed"
                    )}
                  >
                    {format(day, "d")}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
